import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { InstagramMockup, FacebookMockup } from "@/components/studio/AdMockup";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Sparkles, Copy, Save, Loader2, Trash2, Wand2, Check, Star, Columns2, Link2, X, ImageIcon, Download, Camera,
} from "lucide-react";

const SERVICES = ["Freelance Web Development", "Multilingual Translation", "AI Automation Services", "Design Subscriptions", "Virtual Assistants", "Video Editing", "SEO & Content"];
const COUNTRIES = [
  { c: "United States", l: "English (US)" }, { c: "United Kingdom", l: "English (UK)" },
  { c: "Germany", l: "German" }, { c: "France", l: "French" }, { c: "Japan", l: "Japanese" },
  { c: "UAE", l: "Arabic" }, { c: "Brazil", l: "Portuguese" }, { c: "Spain", l: "Spanish" },
];
const ANGLES = ["Trust & quality", "Speed & convenience", "Price / value", "Global talent access", "Results & ROI"];
const TONES = ["Confident and friendly", "Professional & premium", "Playful & bold", "Minimal & direct"];
const OBJECTIVES = ["Awareness", "Traffic", "Lead Generation", "Sales / Conversions"];

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1200); }}
      className="text-zinc-500 hover:text-lime-400 transition-colors shrink-0" data-testid="copy-btn">
      {done ? <Check className="h-3.5 w-3.5 text-lime-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function Line({ text }) {
  return (
    <div className="flex items-start justify-between gap-3 bg-[#0F1315] border border-[#242B30] rounded-lg px-3 py-2.5">
      <span className="text-sm text-zinc-200 leading-relaxed">{text}</span>
      <CopyBtn text={text} />
    </div>
  );
}

export default function Studio() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    service: SERVICES[0], country: "United States", language: "English (US)",
    angle: ANGLES[0], tone: TONES[0], objective: "Traffic",
  });
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(null);
  const [saved, setSaved] = useState([]);
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [linkCreative, setLinkCreative] = useState(null);
  const [campData, setCampData] = useState({ campaigns: [], adsets: [] });
  const [linkForm, setLinkForm] = useState({ campaign_id: "", adset_id: "", name: "" });
  const [promoImage, setPromoImage] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [platform, setPlatform] = useState("instagram");

  const loadSaved = useCallback(async () => {
    const res = await api.get("/ai/creatives");
    setSaved(res.data.creatives);
  }, []);
  const loadCampaigns = useCallback(async () => {
    const res = await api.get("/campaigns");
    setCampData({ campaigns: res.data.campaigns, adsets: res.data.adsets });
  }, []);
  useEffect(() => { loadSaved(); loadCampaigns(); }, [loadSaved, loadCampaigns]);

  const generate = async () => {
    setLoading(true); setContent(null); setPromoImage(null);
    try {
      const res = await api.post("/ai/studio", form);
      setContent(res.data.content);
      toast.success("Creative package generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  const generateImage = async () => {
    if (!content?.image_brief) { toast.error("Generate copy first"); return; }
    setImgLoading(true);
    try {
      const res = await api.post("/ai/studio/image", { brief: content.image_brief, service: form.service, country: form.country });
      setPromoImage(res.data.image);
      toast.success("Promo photo generated 📸");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image generation failed");
    } finally { setImgLoading(false); }
  };

  const downloadImage = () => {
    if (!promoImage) return;
    const a = document.createElement("a");
    a.href = promoImage; a.download = "karyow-promo.png"; a.click();
  };

  const loadCreative = (c) => {
    setContent(c);
    setPromoImage(c?.promo_image || null);
  };

  const save = async () => {
    if (!content) return;
    try {
      await api.post("/ai/creatives", {
        title: `${form.service} · ${form.country}`, ...form,
        content: promoImage ? { ...content, promo_image: promoImage } : content,
      });
      toast.success("Saved to creative library");
      loadSaved();
    } catch (e) { toast.error("Save failed"); }
  };

  const del = async (id) => {
    await api.delete(`/ai/creatives/${id}`);
    setCompareIds((p) => p.filter((x) => x !== id));
    loadSaved(); toast.success("Deleted");
  };

  const toggleWinner = async (c) => {
    await api.put(`/ai/creatives/${c.id}`, { is_winner: !c.is_winner });
    loadSaved();
    toast.success(!c.is_winner ? "Marked as winner 🏆" : "Winner unmarked");
  };

  const toggleCompare = (id) => {
    setCompareIds((p) => p.includes(id) ? p.filter((x) => x !== id) : (p.length >= 3 ? (toast.error("Compare up to 3"), p) : [...p, id]));
  };

  const setCountry = (c) => {
    const found = COUNTRIES.find((x) => x.c === c);
    setForm({ ...form, country: c, language: found?.l || "English" });
  };

  const openLink = (creative) => {
    setLinkCreative(creative);
    const h = (creative.content?.headlines || ["Ad"])[0];
    setLinkForm({ campaign_id: "", adset_id: "", name: `AD_${h.slice(0, 22).toUpperCase().replace(/[^A-Z0-9]+/g, "_")}` });
  };
  const confirmLink = async () => {
    if (!linkForm.adset_id) { toast.error("Select an ad set"); return; }
    try {
      await api.post("/ads/from-creative", { creative_id: linkCreative.id, adset_id: linkForm.adset_id, name: linkForm.name });
      toast.success("Ad created in Campaign Organizer");
      setLinkCreative(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create ad"); }
  };

  const compareList = saved.filter((s) => compareIds.includes(s.id));
  const linkAdsets = campData.adsets.filter((a) => a.campaign_id === linkForm.campaign_id);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 lg:gap-8 animate-fade-up">
      {/* Config */}
      <div className="xl:col-span-4 min-w-0 space-y-4">
        <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-lime-400" />
            <h3 className="font-display font-semibold text-white">Creative Generator</h3>
          </div>
          <div>
            <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Service / Category</Label>
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger data-testid="studio-service" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Target Country</Label>
            <Select value={form.country} onValueChange={setCountry}>
              <SelectTrigger data-testid="studio-country" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                {COUNTRIES.map((s) => <SelectItem key={s.c} value={s.c}>{s.c} · {s.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Angle</Label>
              <Select value={form.angle} onValueChange={(v) => setForm({ ...form, angle: v })}>
                <SelectTrigger data-testid="studio-angle" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                  {ANGLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Objective</Label>
              <Select value={form.objective} onValueChange={(v) => setForm({ ...form, objective: v })}>
                <SelectTrigger data-testid="studio-objective" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                  {OBJECTIVES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Tone</Label>
            <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
              <SelectTrigger data-testid="studio-tone" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                {TONES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button data-testid="generate-btn" onClick={generate} disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-5 min-h-[48px] text-sm font-semibold text-[#0A0D0E] disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Generating with Claude…" : "Generate Creative"}
          </button>
          <p className="text-[10px] text-zinc-600 font-mono text-center">Powered by Claude Sonnet 4.6</p>
        </div>

        {saved.length > 0 && (
          <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold text-white text-sm">Saved Creatives ({saved.length})</h3>
              <button data-testid="open-compare-btn" onClick={() => setCompareOpen(true)} disabled={compareIds.length < 2}
                className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-lime-400/40 text-lime-400 hover:bg-lime-400/10 px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
                <Columns2 className="h-3.5 w-3.5" /> Compare ({compareIds.length})
              </button>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {saved.map((s) => (
                <div key={s.id} data-testid={`creative-${s.id}`} className={`rounded-lg border px-3 py-2 ${compareIds.includes(s.id) ? "border-lime-400/40 bg-lime-400/5" : "border-[#242B30] bg-[#0F1315]"}`}>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" data-testid={`compare-check-${s.id}`} checked={compareIds.includes(s.id)} onChange={() => toggleCompare(s.id)}
                      className="accent-lime-400 h-3.5 w-3.5 shrink-0" />
                    <button className="text-left min-w-0 flex-1" onClick={() => loadCreative(s.content)} data-testid={`load-creative-${s.id}`}>
                      <div className="text-xs text-zinc-200 truncate flex items-center gap-1">
                        {s.is_winner && <Star className="h-3 w-3 text-lime-400 fill-lime-400" />}
                        {s.content?.promo_image && <ImageIcon className="h-3 w-3 text-cyan-400" />}
                        {s.title}
                      </div>
                      <div className="text-[10px] text-zinc-600 font-mono">{s.language} · {s.angle}</div>
                    </button>
                    <button onClick={() => toggleWinner(s)} data-testid={`winner-${s.id}`} title="Mark winner"
                      className={`shrink-0 p-2 ${s.is_winner ? "text-lime-400" : "text-zinc-600 hover:text-lime-400"}`}>
                      <Star className={`h-4 w-4 ${s.is_winner ? "fill-lime-400" : ""}`} />
                    </button>
                    <button onClick={() => openLink(s)} data-testid={`link-creative-${s.id}`} title="Create ad from this"
                      className="shrink-0 p-2 text-zinc-600 hover:text-cyan-400"><Link2 className="h-4 w-4" /></button>
                    <button onClick={() => del(s.id)} data-testid={`delete-creative-${s.id}`}
                      className="shrink-0 p-2 -mr-1 text-zinc-600 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Output */}
      <div className="xl:col-span-8 min-w-0">
        {!content && !loading && (
          <div className="h-full min-h-[400px] flex flex-col items-center justify-center bg-[#14181B] border border-dashed border-[#242B30] rounded-xl text-center p-8">
            <Sparkles className="h-10 w-10 text-zinc-700 mb-4" />
            <p className="text-zinc-400">Configure and generate to see your ad package.</p>
            <p className="text-zinc-600 text-sm mt-1 font-mono">Primary texts · Headlines · CTAs · Video script · Image brief</p>
          </div>
        )}
        {loading && (
          <div className="h-full min-h-[400px] flex items-center justify-center bg-[#14181B] border border-[#242B30] rounded-xl">
            <Loader2 className="h-6 w-6 text-lime-400 animate-spin" />
          </div>
        )}
        {content && (
          <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <h3 className="font-display font-semibold text-white">{form.service}</h3>
                <p className="text-[11px] text-zinc-500 font-mono">{form.country} · {form.language} · {form.angle}</p>
              </div>
              <button data-testid="save-creative-btn" onClick={save}
                className="inline-flex items-center gap-2 rounded-full border border-lime-400/40 text-lime-400 hover:bg-lime-400/10 px-4 py-2 text-xs font-semibold">
                <Save className="h-3.5 w-3.5" /> Save
              </button>
            </div>

            <Tabs defaultValue="primary">
              <TabsList className="bg-[#0F1315] border border-[#242B30] w-full justify-start overflow-x-auto no-scrollbar flex-nowrap sm:flex-wrap h-auto p-1">
                <TabsTrigger value="primary" data-testid="tab-primary" className="data-[state=active]:bg-lime-400 data-[state=active]:text-[#0A0D0E] text-xs shrink-0 whitespace-nowrap min-h-[38px]">Primary Text</TabsTrigger>
                <TabsTrigger value="headlines" data-testid="tab-headlines" className="data-[state=active]:bg-lime-400 data-[state=active]:text-[#0A0D0E] text-xs shrink-0 whitespace-nowrap min-h-[38px]">Headlines</TabsTrigger>
                <TabsTrigger value="cta" data-testid="tab-cta" className="data-[state=active]:bg-lime-400 data-[state=active]:text-[#0A0D0E] text-xs shrink-0 whitespace-nowrap min-h-[38px]">CTAs</TabsTrigger>
                <TabsTrigger value="video" data-testid="tab-video" className="data-[state=active]:bg-lime-400 data-[state=active]:text-[#0A0D0E] text-xs shrink-0 whitespace-nowrap min-h-[38px]">Video Script</TabsTrigger>
                <TabsTrigger value="image" data-testid="tab-image" className="data-[state=active]:bg-lime-400 data-[state=active]:text-[#0A0D0E] text-xs shrink-0 whitespace-nowrap min-h-[38px]">Image Brief</TabsTrigger>
                <TabsTrigger value="photo" data-testid="tab-photo" className="data-[state=active]:bg-lime-400 data-[state=active]:text-[#0A0D0E] text-xs shrink-0 whitespace-nowrap min-h-[38px]">Photo View</TabsTrigger>
              </TabsList>

              <TabsContent value="primary" className="mt-4 space-y-3">
                {content.campaign_brief && (
                  <div className="bg-lime-400/5 border border-lime-400/20 rounded-lg p-3">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-lime-400 mb-1">Campaign Brief</div>
                    <p className="text-sm text-zinc-200">{content.campaign_brief}</p>
                  </div>
                )}
                {(content.primary_texts || []).map((t, i) => <Line key={i} text={t} />)}
              </TabsContent>
              <TabsContent value="headlines" className="mt-4 space-y-2">
                {(content.headlines || []).map((t, i) => <Line key={i} text={t} />)}
              </TabsContent>
              <TabsContent value="cta" className="mt-4 flex flex-wrap gap-2">
                {(content.ctas || []).map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-2 bg-[#0F1315] border border-[#242B30] rounded-full px-4 py-2 text-sm text-zinc-200">
                    {t} <CopyBtn text={t} />
                  </span>
                ))}
              </TabsContent>
              <TabsContent value="video" className="mt-4 space-y-3">
                {content.video_script && (
                  <div className="space-y-2">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-cyan-400">Duration: {content.video_script.duration}</div>
                    {["hook", "body", "cta"].map((seg) => (
                      <div key={seg} className="bg-[#0F1315] border border-[#242B30] rounded-lg p-3">
                        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{seg}</div>
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm text-zinc-200">{content.video_script[seg]}</p>
                          <CopyBtn text={content.video_script[seg]} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="image" className="mt-4 space-y-3">
                <div className="bg-[#0F1315] border border-[#242B30] rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{content.image_brief}</p>
                    <CopyBtn text={content.image_brief} />
                  </div>
                </div>
                {content.compliance_notes && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-amber-400 mb-1">Compliance Note</div>
                    <p className="text-sm text-zinc-300">{content.compliance_notes}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="photo" className="mt-4">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-1 bg-[#0F1315] border border-[#242B30] rounded-lg p-1">
                    <button data-testid="platform-instagram" onClick={() => setPlatform("instagram")}
                      className={`px-4 min-h-[40px] rounded-md text-xs font-mono transition-colors ${platform === "instagram" ? "bg-lime-400 text-[#0A0D0E]" : "text-zinc-400 hover:text-white"}`}>Instagram</button>
                    <button data-testid="platform-facebook" onClick={() => setPlatform("facebook")}
                      className={`px-4 min-h-[40px] rounded-md text-xs font-mono transition-colors ${platform === "facebook" ? "bg-lime-400 text-[#0A0D0E]" : "text-zinc-400 hover:text-white"}`}>Facebook</button>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                    <button data-testid="generate-photo-btn" onClick={generateImage} disabled={imgLoading}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-4 min-h-[44px] text-xs font-semibold text-[#0A0D0E] disabled:opacity-60">
                      {imgLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                      {promoImage ? "Regenerate Photo" : "Generate Promo Photo"}
                    </button>
                    {promoImage && (
                      <button data-testid="download-photo-btn" onClick={downloadImage}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-[#242B30] text-zinc-300 hover:text-white px-4 min-h-[44px] text-xs font-semibold">
                        <Download className="h-3.5 w-3.5" /> Download
                      </button>
                    )}
                  </div>
                </div>

                {imgLoading && !promoImage ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Loader2 className="h-6 w-6 text-lime-400 animate-spin" />
                    <p className="text-xs text-zinc-500 font-mono">Generating promo photo with Nano Banana…</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    {platform === "instagram" ? (
                      <InstagramMockup image={promoImage} brandName={user?.brand_name}
                        logo={user?.logo_url || "/karyow-logo.png"}
                        headline={(content.headlines || ["Discover KARYOW"])[0]}
                        primaryText={(content.primary_texts || [""])[0]}
                        cta={(content.ctas || ["Learn More"])[0]} />
                    ) : (
                      <FacebookMockup image={promoImage} brandName={user?.brand_name}
                        logo={user?.logo_url || "/karyow-logo.png"}
                        headline={(content.headlines || ["Discover KARYOW"])[0]}
                        primaryText={(content.primary_texts || [""])[0]}
                        cta={(content.ctas || ["Learn More"])[0]} />
                    )}
                    {!promoImage && (
                      <p className="mt-3 text-xs text-zinc-600 font-mono text-center">Preview uses your generated copy. Click "Generate Promo Photo" to add an AI image.</p>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      {/* Compare modal */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="bg-[#14181B] border-[#242B30] text-white max-w-5xl w-[calc(100vw-2rem)] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Columns2 className="h-4 w-4 text-lime-400" /> Creative Compare</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Review variations side by side and mark your winner.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 overflow-x-auto" style={{ gridTemplateColumns: `repeat(${Math.max(compareList.length, 1)}, minmax(240px, 1fr))` }}>
            {compareList.map((c) => (
              <div key={c.id} data-testid={`compare-col-${c.id}`} className={`rounded-xl border p-4 ${c.is_winner ? "border-lime-400/50 bg-lime-400/5" : "border-[#242B30] bg-[#0F1315]"}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{c.title}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{c.language} · {c.angle}</div>
                  </div>
                  <button data-testid={`compare-winner-${c.id}`} onClick={() => toggleWinner(c)} className={c.is_winner ? "text-lime-400" : "text-zinc-600 hover:text-lime-400"}>
                    <Star className={`h-4 w-4 ${c.is_winner ? "fill-lime-400" : ""}`} />
                  </button>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Headline</div>
                    <p className="text-zinc-200">{(c.content?.headlines || [])[0]}</p>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Primary Text</div>
                    <p className="text-zinc-300 text-[13px] leading-relaxed">{(c.content?.primary_texts || [])[0]}</p>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">CTA</div>
                    <span className="inline-block rounded-full bg-lime-400/10 border border-lime-400/30 text-lime-400 px-3 py-1 text-xs">{(c.content?.ctas || ["—"])[0]}</span>
                  </div>
                  {c.content?.video_script?.hook && (
                    <div>
                      <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Video Hook</div>
                      <p className="text-zinc-300 text-[13px]">{c.content.video_script.hook}</p>
                    </div>
                  )}
                </div>
                {c.is_winner && <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-lime-400 flex items-center gap-1"><Star className="h-3 w-3 fill-lime-400" /> Winner</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create ad from creative */}
      <Dialog open={!!linkCreative} onOpenChange={(v) => !v && setLinkCreative(null)}>
        <DialogContent className="bg-[#14181B] border-[#242B30] text-white max-w-md w-[calc(100vw-2rem)] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Link2 className="h-4 w-4 text-cyan-400" /> Create Ad from Creative</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Adds a draft ad into an ad set, linked to this creative.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Campaign</Label>
              <Select value={linkForm.campaign_id} onValueChange={(v) => setLinkForm({ ...linkForm, campaign_id: v, adset_id: "" })}>
                <SelectTrigger data-testid="link-campaign" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue placeholder="Select campaign" /></SelectTrigger>
                <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                  {campData.campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Ad Set</Label>
              <Select value={linkForm.adset_id} onValueChange={(v) => setLinkForm({ ...linkForm, adset_id: v })} disabled={!linkForm.campaign_id}>
                <SelectTrigger data-testid="link-adset" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue placeholder={linkForm.campaign_id ? "Select ad set" : "Pick a campaign first"} /></SelectTrigger>
                <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                  {linkAdsets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  {linkForm.campaign_id && linkAdsets.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No ad sets — create one in Campaign Organizer.</div>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Ad Name</Label>
              <Input data-testid="link-ad-name" value={linkForm.name} onChange={(e) => setLinkForm({ ...linkForm, name: e.target.value })}
                className="mt-1.5 bg-[#0F1315] border-[#242B30] font-mono text-sm" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="confirm-link-btn" onClick={confirmLink}
              className="inline-flex items-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-5 py-2.5 text-sm font-semibold text-[#0A0D0E]">
              <Link2 className="h-4 w-4" /> Create Ad
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
